/**
 * HazardManager.ts — Spawns and coordinates the roaming critters.
 *
 * Responsibilities:
 *   - Spawn the level's cats, foxes, squirrels, and pigeons, avoiding
 *     buildings, and let the ones that arrive late wander in on their own
 *     schedule.
 *   - Tick each hazard's wander AI, integrate its motion, and keep it out of
 *     buildings and off the map edges (pure collision, no physics engine).
 *   - Proximity: if any critter comes within HYPER_PULL_RADIUS of the dog, drive
 *     the dog into HYPER_PULL toward it (and release when it leaves) at a
 *     strength that rises as the gap closes — see systems/frenzy.ts.
 *   - Startle: tell every critter the dog is near it, and let each decide what
 *     that is worth (`Hazard.startle`, `HAZARD.REACTION`).
 *   - Flush: when the dog barks, put every pigeon in earshot in the air
 *     (`flushBark`, called from `Game`'s bark handler).
 *
 * That strength, published as {@link HazardManager.pull}, is the input to the
 * frenzy meter as well as to the dog, so one measurement drives the lunge, the
 * barking, the lead's colour and the bar that ends the run.
 *
 * The *what and when* comes from the level (`config/level.ts` → `hazards`); the
 * behaviour here is the same on every level. A spawn with no position written
 * down is scattered at construction, which is what the hand-authored town has
 * always done — see `CritterRelease`.
 */
import * as THREE from 'three';
import { HAZARD } from '../config/constants';
import { activeLevel } from '../config/level';
import { Hazard } from '../entities/Hazard';
import { pointInAnyRect, pointInRect, type Rect } from './level';
import { clampToBounds, resolveCircleRects } from './collision';
import { critterPull } from './frenzy';
import { distanceSq } from '../utils/vec2';
import type { Dog } from '../entities/Dog';
import type { CritterRelease } from './levelgen/types';
import type { HazardType } from '../types';

export class HazardManager {
  readonly hazards: Hazard[] = [];

  /**
   * How hard the nearest critter is pulling on the dog this frame, 0..1.
   *
   * 0 when nothing is in range. Read by `Game` to step the frenzy meter and to
   * tint the lead; the dog is handed the same number directly.
   */
  pull = 0;

  /** Critters not yet due, soonest last so they can be popped off the end. */
  private pending: CritterRelease[] = [];
  private elapsedMs = 0;
  /**
   * The ground the walk happens on — `LevelSpec.bounds`, not the whole map.
   *
   * A generated town carries on past the playable rectangle as scenery, and a
   * critter that wandered out there would be one the dog can never be taken to:
   * it would still count as wildlife on the level while being none of it. So
   * the wildlife bounces off this boundary exactly as it used to bounce off the
   * edge of the map, which on a level with no surround is the same rectangle.
   */
  private readonly bounds: Rect;

  /** Fired when a critter reacts to the dog — by proximity, or to a bark. */
  onCritterStartle?: (type: HazardType) => void;
  /** Fired when a critter triggers dog hyper-pull. */
  onCritterSpotted?: (type: HazardType) => void;

  private lastHyperTarget: Hazard | null = null;
  private startledSet = new Set<Hazard>();

  constructor(
    private scene: THREE.Scene,
    private dog: Dog,
    /** Building footprints: hazards neither spawn inside nor walk through these. */
    private blocked: readonly Rect[] = [],
  ) {
    const level = activeLevel();
    this.bounds = level.bounds;

    // Descending, so the next one due is always the last element.
    const queue = [...level.hazards.spawns].sort((a, b) => b.atMs - a.atMs);
    for (const release of queue) {
      if (release.atMs <= 0) this.release(release);
      else this.pending.push(release);
    }
  }

  /**
   * Bring the next critter due in **now**, in the open near `near`.
   *
   * The guided walk's one direction to this manager: it explains what wildlife
   * does to a dog, and a briefing about critters with no critter anywhere on
   * screen is a briefing about nothing. Rather than inventing an animal, it
   * brings forward one the level had already scheduled — so the level's own
   * count is what it always was, and a critter released here is one that will
   * not also arrive later.
   *
   * Placed a good way off: far enough that the dog does not lock on the instant
   * the card appears, which would teach the lunge before the sentence about it
   * had been read. Falls back to the release's own written-down position if
   * there is no open ground at that distance, and returns whether anything was
   * brought in at all.
   */
  releaseNext(near: { x: number; y: number }, gap = HAZARD.HYPER_PULL_RADIUS * 1.6): boolean {
    const spawn = this.pending.pop();
    if (!spawn) return false;

    // Eight points on a ring, first one in the clear wins. A ring rather than a
    // random scatter because the distance is the part that matters.
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const x = near.x + Math.cos(angle) * gap;
      const y = near.y + Math.sin(angle) * gap;
      const margin = HAZARD.SIZE;
      if (!pointInRect(x, y, this.bounds, -margin)) {
        continue;
      }
      if (pointInAnyRect(x, y, this.blocked, HAZARD.SIZE / 2)) continue;
      this.release({ ...spawn, x, y });
      return true;
    }

    this.release(spawn);
    return true;
  }

  private release(spawn: CritterRelease): void {
    const at =
      spawn.x === undefined || spawn.y === undefined
        ? this.randomSpawn()
        : { x: spawn.x, y: spawn.y };
    const hazard = new Hazard(at.x, at.y, spawn.type);
    this.scene.add(hazard.object);
    this.hazards.push(hazard);
  }

  /**
   * The dog barked at (x, y): put every bird in earshot in the air.
   *
   * Called from `Game`'s bark handler rather than from `update`, because the
   * bark is an *event* — it fires on the dog's own bark timer, and a rule that
   * re-derived "is it barking?" from the pull here would drift out of step with
   * the sound the player is actually hearing.
   *
   * Returns how many took off, which is what the caller needs to decide whether
   * there is a noise to make.
   */
  flushBark(x: number, y: number): number {
    const rangeSq = HAZARD.BARK_FLUSH_RADIUS * HAZARD.BARK_FLUSH_RADIUS;
    let flushed: Hazard | null = null;
    let count = 0;
    for (const hazard of this.hazards) {
      if (distanceSq(hazard.pos, { x, y }) > rangeSq) continue;
      if (!hazard.flush(x, y)) continue;
      flushed = hazard;
      count++;
    }
    // One clatter of wings for the whole lift, not one per bird: four pigeons
    // going up together is a single sound, and four copies of it at the same
    // instant is a burst of noise nobody can place.
    if (flushed) this.onCritterStartle?.(flushed.hazardType);
    return count;
  }

  /** A random point inside the walk that isn't in a building (falls back after tries). */
  private randomSpawn(): { x: number; y: number } {
    for (let attempt = 0; attempt < 20; attempt++) {
      const x = this.bounds.x + HAZARD.SIZE + Math.random() * (this.bounds.w - HAZARD.SIZE * 2);
      const y = this.bounds.y + HAZARD.SIZE + Math.random() * (this.bounds.h - HAZARD.SIZE * 2);
      if (!pointInAnyRect(x, y, this.blocked, HAZARD.SIZE / 2)) return { x, y };
    }
    // Corner fallback (never on a building), in the corner of the *walk*.
    return { x: this.bounds.x + HAZARD.SIZE, y: this.bounds.y + HAZARD.SIZE };
  }

  update(deltaMs: number, dt: number, elapsed: number): void {
    // Late arrivals. A hard level's wildlife builds up over the first half
    // minute rather than being handed over in frame one.
    this.elapsedMs += deltaMs;
    while (this.pending.length > 0 && this.pending[this.pending.length - 1].atMs <= this.elapsedMs) {
      this.release(this.pending.pop() as CritterRelease);
    }

    for (const hazard of this.hazards) {
      hazard.update(deltaMs);
      hazard.integrate(dt);

      // Bounce off buildings and the world edges — except that a bird in the
      // air is *over* the buildings, and bouncing it off a roof it is clearing
      // by fifty units is the one thing that would give the flight away. The
      // world edges still hold: a pigeon that leaves the map never comes back.
      if (!hazard.isFlying && resolveCircleRects(hazard.pos, hazard.radius, this.blocked)) {
        hazard.bounce();
      }
      const before = { x: hazard.pos.x, y: hazard.pos.y };
      clampToBounds(hazard.pos, hazard.radius, this.bounds);
      if (hazard.pos.x !== before.x) hazard.vel.x *= -1;
      if (hazard.pos.y !== before.y) hazard.vel.y *= -1;

      hazard.updateVisuals(dt, elapsed);
    }

    // Critters spook when the dog gets close — independent of the hyper-pull rule,
    // so even a busy dog still scares the wildlife.
    //
    // *What* being spooked means is the critter's own business: a squirrel
    // runs, a fox stands there, a cat holds and then breaks if the dog keeps
    // coming, and a pigeon does not care at all. So this calls every frame the
    // dog is in range — the cat's rule needs the repeat — and lets `startle`
    // report whether that actually started a reaction worth a noise.
    const startleSq = HAZARD.STARTLE_RADIUS * HAZARD.STARTLE_RADIUS;
    for (const hazard of this.hazards) {
      if (distanceSq(hazard.pos, this.dog.pos) > startleSq) {
        this.startledSet.delete(hazard);
        continue;
      }
      const fresh = hazard.startle(this.dog.pos.x, this.dog.pos.y);
      if (fresh && !this.startledSet.has(hazard)) {
        this.startledSet.add(hazard);
        this.onCritterStartle?.(hazard.hazardType);
      }
    }

    // Don't let hazards yank the dog while it's mid-poop — and with nothing
    // able to wind it up, the frenzy meter gets a breather too.
    if (this.dog.isBusy) {
      this.lastHyperTarget = null;
      this.pull = 0;
      return;
    }

    // Find the nearest critter within the hyper-pull radius of the dog. Every
    // kind counts: a fox in the hedge is exactly as interesting to a dog as a
    // cat on the wall, and with the old leash-tangle gone there is no reason
    // left for one of them to be inert.
    //
    // A pigeon **in the air** is the single exception, and it is the point of
    // the bird rather than a special case bolted onto it: the dog barks, the
    // flock goes up, and the thing it was barking at is suddenly out of reach.
    // The pull drops, the meter breathes, and the player has learned that
    // barking at pigeons is the one distraction that solves itself.
    let nearest: Hazard | null = null;
    let nearestDistSq = HAZARD.HYPER_PULL_RADIUS * HAZARD.HYPER_PULL_RADIUS;
    for (const hazard of this.hazards) {
      if (hazard.isFlying) continue;
      const dSq = distanceSq(hazard.pos, this.dog.pos);
      if (dSq <= nearestDistSq) {
        nearestDistSq = dSq;
        nearest = hazard;
      }
    }

    if (nearest) {
      this.pull = critterPull(Math.sqrt(nearestDistSq), HAZARD.HYPER_PULL_RADIUS);
      if (this.lastHyperTarget !== nearest) {
        this.lastHyperTarget = nearest;
        this.onCritterSpotted?.(nearest.hazardType);
      }
      this.dog.enterHyperPull(nearest.pos.x, nearest.pos.y, this.pull);
    } else {
      this.pull = 0;
      this.lastHyperTarget = null;
      this.dog.releaseHyperPull();
    }
  }
}
